// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_CORE_URL;
const API_ENDPOINT = '/api/UserAuditLog';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_CORE_SCOPE;

const HEADERS = {
  'Content-Type': 'application/json',
};

// ============= Types =============

export interface UserAuditLog {
  startDate: string;
  endDate: string;
  results: string;
  offset: string;
  users: string[];
}

export interface UserAuditLogEntry {
  date: string;
  entityType: string;
  action: string;
  entityId: string;
  entityName: string;
  /** Present when API returns logs for multiple users */
  userId?: string;
}

export interface UserAuditLogResponse {
  userId: string;
  logs: UserAuditLogEntry[];
}

export interface PostUserAuditLogWorkspaceProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  workspaceId: string;
  body: UserAuditLog;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Creates or updates a user
 * @param getAccessToken Function to acquire access token
 * @param workspaceId Workspace ID
 * @param body Optional request body
 * @returns Promise with API response or error
 */
export const postUserAuditLogWorkspace = async ({
  getAccessToken,
  workspaceId,
  body,
}: PostUserAuditLogWorkspaceProps): Promise<UserAuditLogResponse> => {
  try {
    console.log('🔄 Posting user audit log...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/workspace/${workspaceId}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'POST',
      headers: createHeaders(token),
      body: body ? JSON.stringify(body) : undefined,
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ User audit log posted successfully: ${data}`);

    return data;
  } catch (error) {
    console.error('🔴 Error posting user audit log:', error);
    throw error;
  }
};
